<script setup>
import { useShareLink } from "@/composables/useShareLink";
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";
import ExpertListCover from "@/Components/Profile/ExpertListCover.vue";
import { useExpertListHeaderImage } from "@/composables/useExpertListHeaderImage.js";

const props = defineProps({
    list: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["remove"]);

const isExpert = computed(() => props.list.type === "expert");
// A Gift Hero's expert list gets the editorial cover art (GH-1299).
const isGiftHeroExpert = computed(
    () => isExpert.value && !!props.list.owner?.is_gift_hero,
);

// image_url arrives as an array of Cloudinary size variants on profile
// pages (and a plain string elsewhere) — normalize to one URL, and treat
// Cloudinary's blank "missing-image" fallback as no cover (same as
// ExpertCard, GH-1062).
const coverUrl = computed(() => {
    const raw = props.list.image_url;
    return Array.isArray(raw) ? raw[0] : raw;
});
const hasCover = computed(
    () => !!coverUrl.value && !coverUrl.value.includes("missing-image"),
);

// An expert list with a header image shows that on its focal point instead
// of the item photo (GH-1535); a 404 falls back to the cover below.
const { headerImage, headerFailed } = useExpertListHeaderImage(
    () => props.list,
);

const ownerName = computed(() => props.list.owner?.presentation_name);

const { shareLink } = useShareLink();

async function shareList() {
    // Tag the link before branching so one press mints one token,
    // whichever path the browser takes (GH-1192).
    const channel = navigator.share ? "share_sheet" : "copy_link";
    const url = shareLink(
        props.list.url,
        { type: "giftlist", uuid: props.list.uuid },
        channel,
    );

    if (navigator.share) {
        try {
            await navigator.share({ title: props.list.name, url });
        } catch {
            // User dismissed the share sheet — nothing to do.
        }

        return;
    }

    await navigator.clipboard?.writeText(url);
}
</script>

<template>
    <article class="relative flex gap-4 rounded-[4px] bg-white p-[13px]">
        <span
            v-if="isExpert"
            class="absolute left-0 top-[13px] inline-flex items-center gap-1.5 rounded-r-[2px] bg-brand-navy py-[5px] pl-[26px] pr-3.5 font-brand text-[12px] font-bold tracking-wide text-white"
        >
            <svg
                viewBox="0 0 12 15"
                class="h-3.5 w-3"
                fill="none"
                aria-hidden="true"
            >
                <path
                    d="M5.91117 0L0 2.61013V6.52532C0 10.1469 2.5221 13.5335 5.91117 14.3557C9.30024 13.5335 11.8223 10.1469 11.8223 6.52532V2.61013L5.91117 0Z"
                    fill="url(#ghShieldBookmarkTag)"
                />
                <path
                    d="M9.05888 4.22504L6.64465 4.68453C6.54944 4.70158 6.47969 4.79012 6.47969 4.89384V6.8979C6.45698 6.90398 6.43381 6.91005 6.40949 6.91542C6.26495 6.94813 6.09403 6.96798 5.91141 6.96798C5.74554 6.96798 5.58907 6.95163 5.45348 6.92407C5.41448 6.91612 5.37777 6.90701 5.34267 6.89767V4.89407C5.34267 4.79035 5.27292 4.70182 5.17771 4.68477L2.76394 4.22527C2.64326 4.20355 2.5336 4.30329 2.5336 4.43458V9.46144C2.5336 9.56516 2.60334 9.6537 2.69856 9.67075L5.11233 10.1302C5.233 10.152 5.34267 10.0522 5.34267 9.92094V8.16332C5.52414 8.19276 5.71457 8.20794 5.91141 8.20817C6.10803 8.20817 6.29822 8.19252 6.47947 8.16332V9.92117C6.47947 10.0525 6.58936 10.1522 6.71004 10.1305L9.12427 9.67098C9.21948 9.65393 9.28922 9.5654 9.28922 9.46168V4.43481C9.28922 4.30353 9.17933 4.20378 9.05865 4.22551L9.05888 4.22504Z"
                    fill="#102A3D"
                />
                <defs>
                    <linearGradient
                        id="ghShieldBookmarkTag"
                        x1="4.22226"
                        y1="1.19631"
                        x2="11.1383"
                        y2="16.6556"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stop-color="#FFB545" />
                        <stop offset="1" stop-color="#DB7712" />
                    </linearGradient>
                </defs>
            </svg>
            EXPERT LIST
        </span>

        <div
            class="flex min-w-0 flex-1 flex-col pb-2 pl-3"
            :class="isExpert ? 'pt-[50px]' : 'pt-2.5'"
        >
            <Link
                :href="list.url"
                class="line-clamp-2 break-words font-brand text-[18px] font-semibold leading-[1.3] text-brand-navy transition hover:text-brand-red"
            >
                {{ list.name }}
            </Link>
            <p
                v-if="list.description"
                class="mt-1.5 line-clamp-2 font-brand text-[13px] leading-[1.4] text-[#565a77]"
            >
                {{ list.description }}
            </p>

            <div class="mt-2 flex items-center gap-1">
                <button
                    type="button"
                    class="grid size-7 place-items-center rounded-[4px] text-[#565a77]/70 transition hover:bg-brand-cream hover:text-brand-navy"
                    aria-label="Share list"
                    @click="shareList"
                >
                    <svg
                        viewBox="0 0 13 14"
                        class="h-3.5 w-[13px]"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.12"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path
                            d="M8.2764 2.6185L6.17224 0.561096L4.06807 2.6185M6.17224 0.561096V8.81633M2.66545 5.36171C2.01184 5.36171 1.68504 5.36171 1.42724 5.46609C1.08353 5.60531 0.810281 5.8725 0.667906 6.20861C0.561127 6.46064 0.561127 6.78002 0.561127 7.41912V10.711C0.561127 11.4791 0.561127 11.863 0.714022 12.1564C0.848507 12.4144 1.06295 12.6247 1.3269 12.7562C1.62668 12.9055 2.01931 12.9055 2.8034 12.9055H9.54143C10.3255 12.9055 10.7176 12.9055 11.0174 12.7562C11.2813 12.6247 11.4961 12.4144 11.6306 12.1564C11.7833 11.8633 11.7833 11.4798 11.7833 10.7131V7.41912C11.7833 6.78002 11.7833 6.46064 11.6765 6.20861C11.5341 5.8725 11.2611 5.60531 10.9174 5.46609C10.6596 5.36171 10.3328 5.36171 9.67918 5.36171"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    class="grid size-7 place-items-center rounded-[4px] text-[#565a77] transition hover:bg-brand-cream hover:text-brand-red"
                    aria-label="Remove from Saved Lists"
                    @click="emit('remove', list)"
                >
                    <svg
                        viewBox="0 0 13 14"
                        class="h-3.5 w-[13px]"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.12"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path
                            d="M10.3805 2.61863V9.61381C10.3805 10.7661 10.3805 11.3422 10.1512 11.7823C9.94947 12.1695 9.6276 12.4842 9.23167 12.6814C8.78151 12.9057 8.19235 12.9057 7.01387 12.9057H5.33054C4.1521 12.9057 3.56287 12.9057 3.11277 12.6814C2.71684 12.4842 2.39495 12.1695 2.19321 11.7823C1.96387 11.3422 1.96387 10.7661 1.96387 9.61381V2.61863M0.561096 2.61863H11.7833M8.97776 2.61863L8.78797 2.0618C8.60399 1.5222 8.51197 1.2524 8.34139 1.05292C8.19073 0.876777 7.99729 0.740432 7.77874 0.656394C7.53122 0.561218 7.24042 0.561218 6.65869 0.561218H5.68572C5.10399 0.561218 4.8132 0.561218 4.56568 0.656394C4.34714 0.740432 4.15367 0.876777 4.00302 1.05292C3.83241 1.2524 3.74044 1.5222 3.55648 2.0618L3.36665 2.61863"
                        />
                    </svg>
                </button>
            </div>

            <div class="mt-auto flex items-center gap-2.5 pt-3">
                <span
                    class="size-[25px] shrink-0 overflow-hidden rounded-[2px] bg-brand-tan/40"
                    aria-hidden="true"
                >
                    <img
                        v-if="list.owner?.avatar?.profile_photo"
                        :src="list.owner.avatar.profile_photo"
                        alt=""
                        class="size-full object-cover"
                    />
                </span>
                <span
                    class="truncate font-brand text-[14px] font-medium text-[#102a3d]"
                >
                    {{ ownerName }}
                </span>
            </div>
        </div>

        <Link
            :href="list.url"
            class="grid w-[110px] shrink-0 place-items-center self-stretch overflow-hidden rounded-[2px] bg-brand-tan/30 sm:w-[140px]"
        >
            <img
                v-if="headerImage"
                :src="headerImage.tile2xSrc"
                :alt="list.name"
                class="size-full object-cover"
                :style="{ objectPosition: headerImage.position }"
                data-test="list-header-photo"
                @error="headerFailed = true"
            />
            <ExpertListCover
                v-else-if="isGiftHeroExpert"
                :image="hasCover ? coverUrl : null"
                layout="tile"
            />
            <img
                v-else-if="hasCover"
                :src="coverUrl"
                :alt="list.name"
                class="size-full object-cover"
            />
            <!-- Cover-less lists (including Cloudinary's missing-image
                 fallback URL) show the subdued logo bug, matching the
                 ExpertCard placeholder (GH-1062). -->
            <img
                v-else
                :src="asset('images/brand/logo-mark.png')"
                alt=""
                aria-hidden="true"
                class="size-10 opacity-25 grayscale"
            />
        </Link>
    </article>
</template>
